import type { EvaluationCriteriaEducationYear, EvaluationCriteriaSubjectOption } from 'app/types/evaluationCriteria';

export type CatalogDiscipline = {
  guid: string;
  name?: string;
  discipline?: string;
};

/** Opción del select: el valor es el guid de la disciplina, que es lo que se busca. */
export type DisciplineOption = {
  label: string;
  value: string;
};

type RawEducationYear = Partial<EvaluationCriteriaEducationYear> | null | undefined;

/** Fila tal como puede llegar del back: el contrato no protege de nulos ni de subjects repetidos. */
export type RawSubjectOption =
  | {
      subject?: string | null;
      disciplineIds?: Array<string | null | undefined> | null;
      educationYears?: RawEducationYear[] | null;
    }
  | null
  | undefined;

type ResolveSelectedDisciplineParams = {
  currentDisciplineId: string | null;
  preselectedDisciplineId?: string;
  subjects: EvaluationCriteriaSubjectOption[];
};

// Primaria y Secundaria de Edelvives, el único tenant que monta el diálogo. Cada colegio usa el catálogo de etapas de
// su centro (genérico, España, México y Colombia en `es_edv/common.json` y en los datos de dev), así que van los ids de
// todos. Lista de permitidos y no de excluidos: una etapa nueva del backend no debe colarse en el selector sin decidirlo.
const ALLOWED_EDUCATION_LEVELS = new Set(['el2', 'el3', 'el-es-02', 'el-es-03', 'el-mx-02', 'el-mx-03', 'el-co-01', 'el-co-02']);

function getDisciplineLabel(discipline: CatalogDiscipline): string {
  return discipline?.discipline ?? discipline?.name ?? '';
}

function isSameEducationYear(a: EvaluationCriteriaEducationYear, b: EvaluationCriteriaEducationYear): boolean {
  return a.educationLevel === b.educationLevel && a.educationYear === b.educationYear;
}

function addDisciplineIds(target: string[], ids: RawSubjectOption['disciplineIds']): void {
  (ids ?? []).forEach((id) => {
    if (id && !target.includes(id)) target.push(id);
  });
}

function addAllowedEducationYears(target: EvaluationCriteriaEducationYear[], pairs: RawEducationYear[] | null | undefined): void {
  (pairs ?? []).forEach((pair) => {
    if (!pair?.educationLevel || !pair?.educationYear || !ALLOWED_EDUCATION_LEVELS.has(pair.educationLevel)) return;
    const next = { educationLevel: pair.educationLevel, educationYear: pair.educationYear };
    if (!target.some((existing) => isSameEducationYear(existing, next))) target.push(next);
  });
}

/**
 * Deja la respuesta del back lista para el resto de funciones: un subject por elemento (los repetidos se fusionan),
 * sin nulos ni repetidos y solo con cursos de Primaria y Secundaria. Un subject sin cursos permitidos no sale.
 */
export function normalizeSubjects(raw: RawSubjectOption[] | null | undefined): EvaluationCriteriaSubjectOption[] {
  const bySubject = new Map<string, EvaluationCriteriaSubjectOption>();
  (raw ?? []).forEach((item) => {
    if (!item?.subject) return;
    const merged = bySubject.get(item.subject) ?? { subject: item.subject, disciplineIds: [], educationYears: [] };
    addDisciplineIds(merged.disciplineIds, item.disciplineIds);
    addAllowedEducationYears(merged.educationYears, item.educationYears);
    bySubject.set(item.subject, merged);
  });
  return Array.from(bySubject.values()).filter((item) => item.educationYears.length > 0);
}

// El back resuelve un solo subject por disciplina; si un guid llegara en varios, vale el primero en el que aparece.
function findSubjectOfDiscipline(subjects: EvaluationCriteriaSubjectOption[], disciplineId: string): EvaluationCriteriaSubjectOption | null {
  if (!disciplineId) return null;
  return subjects.find((item) => item.disciplineIds.includes(disciplineId)) ?? null;
}

/**
 * Una opción por disciplina de los subjects de la comunidad que exista en el catálogo del tenant, con su nombre. Dos
 * disciplinas con el mismo nombre salen las dos: son distintas y el back las resuelve por guid.
 */
export function getDisciplineOptions(subjects: EvaluationCriteriaSubjectOption[], catalog: CatalogDiscipline[] | null | undefined): DisciplineOption[] {
  const catalogById = new Map((catalog ?? []).filter((discipline) => discipline?.guid).map((discipline) => [discipline.guid, discipline]));
  const disciplineIds = new Set(subjects.flatMap((item) => item.disciplineIds));

  return Array.from(disciplineIds)
    .reduce<DisciplineOption[]>((options, id) => {
      const discipline = catalogById.get(id);
      return discipline ? [...options, { label: getDisciplineLabel(discipline), value: id }] : options;
    }, [])
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Cursos del subject de la disciplina: dos disciplinas del mismo subject comparten cursos. */
export function getAllowedEducationYears(subjects: EvaluationCriteriaSubjectOption[], disciplineId: string): EvaluationCriteriaEducationYear[] {
  return findSubjectOfDiscipline(subjects, disciplineId)?.educationYears ?? [];
}

/** Etapa y curso se comparan juntos: el id de curso se repite entre etapas. */
export function isEducationYearAllowed(
  allowed: EvaluationCriteriaEducationYear[],
  value: Partial<EvaluationCriteriaEducationYear> | null | undefined
): boolean {
  if (!value?.educationLevel || !value?.educationYear) return false;
  return allowed.some((pair) => isSameEducationYear(pair, value as EvaluationCriteriaEducationYear));
}

/**
 * Disciplina elegida en la comunidad, decidida solo con su respuesta (no con el catálogo, que puede llegar tarde y
 * solo pone la etiqueta). `currentDisciplineId` es `null` mientras el usuario no ha elegido nada: entonces vale la
 * disciplina preseleccionada en el formulario.
 */
export function resolveSelectedDiscipline({ currentDisciplineId, preselectedDisciplineId, subjects }: ResolveSelectedDisciplineParams): string {
  const candidate = currentDisciplineId ?? preselectedDisciplineId ?? '';
  return findSubjectOfDiscipline(subjects, candidate) ? candidate : '';
}
